import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Megaphone, CheckCircle2, ShieldCheck, Sparkles } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  advertiser: "",
  headline: "",
  body: "",
  cta_text: "Learn more",
  link_url: "https://",
  accent_color: "#C85A17",
  placement: "top",
  image_path: "",
  video_url: "",
  submitter_email: "",
  submitter_name: "",
  submitter_phone: "",
};

export default function SubmitAd() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    ...EMPTY,
    submitter_email: user?.email || "",
    submitter_name: user?.name || "",
    submitter_phone: user?.phone || "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    if (!form.advertiser.trim() || !form.headline.trim() || !form.link_url.trim()
        || !form.submitter_email.trim() || !form.submitter_name.trim()) {
      toast.error("Advertiser, headline, link, your name and email are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/ads/submit`, form);
      setDone(true);
      toast.success("Submitted! We'll review within 24 hours.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 text-green-700">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <h1 className="mt-6 font-display text-3xl font-bold text-slate-900">Submission received</h1>
        <p className="mt-3 text-slate-600">
          Thanks {form.submitter_name.split(" ")[0]} — the EasyStay team reviews new ads within 24 hours.
          You&apos;ll get an email at <b>{form.submitter_email}</b> once a decision is made.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button onClick={() => { setDone(false); setForm({ ...EMPTY, submitter_email: form.submitter_email, submitter_name: form.submitter_name, submitter_phone: form.submitter_phone }); }}
            data-testid="submit-another-btn" className="btn-outline text-sm">Submit another</button>
          <Link to="/" className="btn-primary text-sm">Back to EasyStay</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8">
      {/* Hero */}
      <div className="rounded-3xl bg-forest p-8 text-white lg:p-12">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-copper">
          <Megaphone className="h-4 w-4" /> Advertise on EasyStay
        </div>
        <h1 className="mt-3 font-display text-3xl font-bold sm:text-5xl">
          Reach travellers across Zambia
        </h1>
        <p className="mt-4 max-w-2xl text-white/80">
          Get in front of thousands of guests browsing hotels, lodges and guest houses. Ads run as top or bottom promotional strips across every EasyStay page — telecom, retail, food &amp; beverage, electronics, tourism… any category is welcome.
        </p>
        <p className="mt-3 text-sm text-white/80">
          Before you submit, please read the <Link to="/advertise/guidelines" data-testid="advertise-guidelines-link" className="font-semibold text-copper underline underline-offset-2 hover:opacity-90">ad content guidelines</Link> so your submission gets approved quickly.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Sparkles, title: "Instant preview", body: "See exactly how your ad will look before you submit." },
            { icon: ShieldCheck, title: "24-hour review", body: "Our team screens every submission for quality & safety." },
            { icon: CheckCircle2, title: "Pay after approval", body: "Only pay once your ad is approved and live." },
          ].map((f, i) => (
            <div key={i} className="rounded-2xl bg-white/10 p-4 backdrop-blur">
              <f.icon className="h-5 w-5 text-copper" />
              <div className="mt-2 text-sm font-bold">{f.title}</div>
              <div className="mt-1 text-xs text-white/70">{f.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Form */}
      <div className="mt-10 rounded-3xl bg-white p-8 card-shadow">
        <h2 className="font-display text-2xl font-bold text-slate-900">Submit your ad</h2>
        <p className="mt-1 text-sm text-slate-600">
          Fields marked <span className="text-rose-600">*</span> are required. Keep headlines under 90 characters for best display.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="Business / advertiser name *" testid="submit-advertiser"
            value={form.advertiser} onChange={(v) => update({ advertiser: v })} />
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Placement</label>
            <select
              data-testid="submit-placement"
              value={form.placement}
              onChange={(e) => update({ placement: e.target.value })}
              className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
            >
              <option value="top">Top strip (above navbar)</option>
              <option value="bottom">Bottom strip (above footer)</option>
            </select>
          </div>
        </div>

        <div className="mt-4">
          <Field label="Headline *" testid="submit-headline"
            value={form.headline} onChange={(v) => update({ headline: v })}
            placeholder="e.g. 10GB data bundle for K99 — this weekend only" />
        </div>
        <div className="mt-4">
          <Field label="Sub-text (optional)" testid="submit-body"
            value={form.body} onChange={(v) => update({ body: v })}
            placeholder="Optional extra line, shown on wider screens" />
        </div>

        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <Field label="Call-to-action text" testid="submit-cta"
            value={form.cta_text} onChange={(v) => update({ cta_text: v })}
            placeholder="e.g. Learn more, Shop now, Book" />
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500">Accent colour</label>
            <div className="mt-1 flex items-center gap-2">
              <input type="color" value={form.accent_color}
                onChange={(e) => update({ accent_color: e.target.value })}
                data-testid="submit-color"
                className="h-10 w-14 cursor-pointer rounded-lg border border-slate-200" />
              <input value={form.accent_color}
                onChange={(e) => update({ accent_color: e.target.value })}
                className="flex-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 font-mono text-sm outline-none focus:border-copper focus:bg-white" />
            </div>
          </div>
        </div>

        <div className="mt-4">
          <Field label="Destination URL *" testid="submit-link"
            value={form.link_url} onChange={(v) => update({ link_url: v })}
            placeholder="https://your-site.com/promo" />
          <p className="mt-1 text-xs text-slate-500">Where visitors will land when they tap your ad. Must start with https://</p>
        </div>

        {form.placement === "top" && (
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Image URL (optional)</label>
              <input
                data-testid="submit-image-url"
                value={form.image_path}
                onChange={(e) => update({ image_path: e.target.value, video_url: "" })}
                placeholder="https://images.unsplash.com/…"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs outline-none focus:border-copper focus:bg-white"
              />
              <p className="mt-1 text-xs text-slate-500">Direct link to a public image (Unsplash / your CDN).</p>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Video URL (optional, overrides image)</label>
              <input
                data-testid="submit-video-url"
                value={form.video_url}
                onChange={(e) => update({ video_url: e.target.value })}
                placeholder="https://…/promo.mp4"
                className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-xs outline-none focus:border-copper focus:bg-white"
              />
              <p className="mt-1 text-xs text-slate-500">Direct link to mp4/webm. Autoplays muted.</p>
            </div>
          </div>
        )}

        {/* Live preview */}
        <div className="mt-6">
          <div className="mb-2 text-xs font-semibold uppercase tracking-widest text-slate-500">Live preview</div>
          <div className="overflow-hidden rounded-xl" style={{ background: form.accent_color }}>
            <div className="flex items-center gap-3 px-4 py-2 text-sm">
              <span className="rounded-full bg-black/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">Sponsored</span>
              <div className="flex-1 truncate font-semibold text-white">
                <span className="text-white/70 mr-1">{form.advertiser || "Your business"} ·</span>{form.headline || "Your headline appears here"}
              </div>
              <span className="rounded-full bg-white/95 px-3 py-1 text-xs font-bold text-slate-900">{form.cta_text || "Learn more"}</span>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-8 rounded-2xl bg-sand/60 p-5">
          <h3 className="font-display text-base font-bold text-slate-900">Your contact details</h3>
          <p className="mt-1 text-xs text-slate-600">We&apos;ll email you the approval decision and any follow-ups.</p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Your name *" testid="submit-your-name"
              value={form.submitter_name} onChange={(v) => update({ submitter_name: v })} />
            <Field label="Email *" testid="submit-your-email" type="email"
              value={form.submitter_email} onChange={(v) => update({ submitter_email: v })} />
          </div>
          <div className="mt-4">
            <Field label="Phone (optional)" testid="submit-your-phone"
              value={form.submitter_phone} onChange={(v) => update({ submitter_phone: v })}
              placeholder="+260 …" />
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-slate-500">
            By submitting you agree that EasyStay may reject ads that violate our content or safety guidelines.
          </p>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="submit-ad-btn"
            className="btn-primary text-sm"
          >
            {submitting ? "Submitting…" : "Submit for review"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, placeholder, type = "text" }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-500">{label}</label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm outline-none focus:border-copper focus:bg-white"
      />
    </div>
  );
}
